/**
 * Snapshot form of the document (W1-03 core-model-ops; contract `SerializedChartDoc`).
 *
 * `serializeDoc`: canonical and exact — tables as arrays sorted by id (numeric ids ascending, asset ids by code
 * unit), Beats as `{w, n, d}` objects, passthrough as pair lists, `undefined` fields dropped. Records are shared
 * with the document (they are immutable), never copied unless a field had to be dropped. Numbers are kept as they
 * are: the snapshot of any document held by a container is plain-JSON safe, because a document never holds -0
 * (containers and TxDraft writes map it to 0, core/ops/equal.ts). A hand-assembled document that still holds -0
 * keeps it here; structured clone and the lossless snapshot codec of src/persist carry it, plain JSON writes 0.
 *
 * `deserializeDoc`: migrates older versions (core/doc/migrate.ts), validates the whole snapshot against the record
 * shapes (core/model/schema.ts: types, enums, integer fields, normalised beats, no unknown fields) and rejects
 * duplicate ids, throwing a `DocFormatError` with precise paths such as `keys[12].ease.left: expected a finite
 * number, got a string`. Model invariants (references, ordering, hold rule...) are NOT checked here; callers run
 * `checkInvariants` (dev builds, tests, after journal replay).
 */

import { stripUndefined } from '../ops/equal';
import { checkShape, snapshotShape } from '../model/schema';
import type {
  ChartDoc,
  DeserializeDocFn,
  SerializeDocFn,
  SerializedChartDoc,
  TableIds,
  TableName,
  TableRecords,
} from '../model/types';
import { DocFormatError, docFormatProblem, migrateSnapshot, type DocFormatProblem } from './migrate';

export { DocFormatError } from './migrate';

/** Max problems collected before giving up (the error lists them). */
const MAX_PROBLEMS = 20;

function compareIds(a: number | string, b: number | string): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  const x = String(a);
  const y = String(b);
  return x < y ? -1 : x > y ? 1 : 0;
}

function tableArray<T extends TableName>(table: ReadonlyMap<TableIds[T], TableRecords[T]>): TableRecords[T][] {
  const out: TableRecords[T][] = [];
  for (const rec of table.values()) out.push(stripUndefined(rec));
  out.sort((a, b) => compareIds(a.id, b.id));
  return out;
}

export const serializeDoc: SerializeDocFn = (doc) => ({
  kind: 'phiedit-doc',
  version: 1,
  idSeq: doc.idSeq,
  meta: stripUndefined(doc.meta),
  pkg: stripUndefined(doc.pkg),
  timing: stripUndefined(doc.timing),
  editor: stripUndefined(doc.editor),
  rpeRoot: stripUndefined(doc.rpeRoot),
  bpm: tableArray<'bpm'>(doc.bpm),
  lines: tableArray<'lines'>(doc.lines),
  lineOrder: [...doc.lineOrder],
  tracks: tableArray<'tracks'>(doc.tracks),
  keys: tableArray<'keys'>(doc.keys),
  notes: tableArray<'notes'>(doc.notes),
  effects: tableArray<'effects'>(doc.effects),
  effectOrder: [...doc.effectOrder],
  videos: tableArray<'videos'>(doc.videos),
  markers: tableArray<'markers'>(doc.markers),
  groups: tableArray<'groups'>(doc.groups),
  generators: tableArray<'generators'>(doc.generators),
  assets: tableArray<'assets'>(doc.assets),
});

function toMap<T extends TableName>(
  tbl: T,
  list: readonly TableRecords[T][],
  problems: DocFormatProblem[],
): Map<TableIds[T], TableRecords[T]> {
  const m = new Map<TableIds[T], TableRecords[T]>();
  const firstAt = new Map<TableIds[T], number>();
  list.forEach((raw, i) => {
    const rec = stripUndefined(raw);
    const id = rec.id as TableIds[T];
    const prev = firstAt.get(id);
    if (prev !== undefined) {
      if (problems.length < MAX_PROBLEMS) {
        problems.push(
          docFormatProblem(`${tbl}[${String(i)}].id`, 'snapshot.duplicateId', {
            id: String(id),
            other: `${tbl}[${String(prev)}]`,
          }),
        );
      }
      return;
    }
    firstAt.set(id, i);
    m.set(id, rec);
  });
  return m;
}

export const deserializeDoc: DeserializeDocFn = (json) => {
  const migrated = migrateSnapshot(json);
  const shapeProblems = checkShape(snapshotShape, migrated, '', MAX_PROBLEMS);
  if (shapeProblems.length > 0) {
    throw new DocFormatError(
      shapeProblems.map((p) => ({
        path: p.path.replace(/^\./, '') || '(root)',
        problem: p.problem,
        code: 'model.shape.problem',
        params: p.params,
      })),
      shapeProblems.length >= MAX_PROBLEMS,
    );
  }
  const s = migrated as SerializedChartDoc;
  const problems: DocFormatProblem[] = [];
  const doc: ChartDoc = {
    kind: 'phiedit-doc',
    version: 1,
    idSeq: s.idSeq,
    meta: stripUndefined(s.meta),
    pkg: stripUndefined(s.pkg),
    timing: stripUndefined(s.timing),
    editor: stripUndefined(s.editor),
    rpeRoot: stripUndefined(s.rpeRoot),
    bpm: toMap('bpm', s.bpm, problems),
    lines: toMap('lines', s.lines, problems),
    lineOrder: [...s.lineOrder],
    tracks: toMap('tracks', s.tracks, problems),
    keys: toMap('keys', s.keys, problems),
    notes: toMap('notes', s.notes, problems),
    effects: toMap('effects', s.effects, problems),
    effectOrder: [...s.effectOrder],
    videos: toMap('videos', s.videos, problems),
    markers: toMap('markers', s.markers, problems),
    groups: toMap('groups', s.groups, problems),
    generators: toMap('generators', s.generators, problems),
    assets: toMap('assets', s.assets, problems),
  };
  if (problems.length > 0) throw new DocFormatError(problems, problems.length >= MAX_PROBLEMS);
  return doc;
};
